/**
 * 완료 모임 화면 — 모임 종료 60분 후 또는 주최자 명시 종료 시 노출.
 * 정기 모임(isRegularMeeting)은 본 화면 적용 외 (별도 기획).
 *
 * 섹션:
 *  ① Hero — 한줄 요약
 *  ② 타임라인 — sub_place 시간순 카드 (어디서 어떻게 진행했는가)
 *  ③ 정산 카드 — 미정산 / 받을 돈 / 보낼 돈 + 정산 화면 CTA
 *  ④ 참여자 — 도착 통계 + 정산 상태, 주최자만 ⋯ 메뉴 안에 [내보내기]
 *  ⑤ 후기 — 사진 + 댓글 + 좋아요, 카드/댓글 ⋯ 메뉴 안에 [신고]
 *  Footer — [다시 만들기] [공유하기]
 */
import React, { useEffect, useRef, useState } from 'react';
import i18n from 'i18next';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { useUserInfo } from 'hooks/useAuth';
import { MeetingRepository } from 'repository/meetingRepository';
import { PaymentRepository } from 'repository/paymentRepository';
import { MeetingPostsSection } from './MeetingPostsSection';
import { PlacePhotoPeek } from 'components/place/PlacePhotoPeek';
import { feedPhotos } from 'hooks/usePlaceReviews';
import type { PlaceFeed } from 'hooks/usePlaceReviews';
import { formatPaymentLine } from 'common/utils/paymentStoreName';
import { formatPaymentAmount } from 'common/utils/treatPhrases';
import { CostInput } from 'pages/cost/CostInput';
import {
    AppScreen, Section, SectionHeader, Button, Avatar, InverseBanner, Sheet,
    Bleed, HeroBanner,
} from 'components/ds';
import styles from './MeetingFinishedView.module.css';

// 백엔드에 URL-encoded 로 저장되는 nickname 을 안전하게 디코드.
// 이미 decoded 거나 잘못된 시퀀스면 원본 그대로 반환.
const safeDecode = (s: any): any => {
    if (typeof s !== 'string' || !s) return s;
    try { return decodeURIComponent(s); } catch { return s; }
};

interface Props {
    meeting: any;
    /** 장소 사진·후기 — 부모가 한 번 받아 넘깁니다 (여기서 또 부르면 두 번 조회됩니다) */
    /** 장소 사진·후기. 부모가 한 번 받아 넘깁니다 (여기서 또 부르면 두 번 조회됩니다).
        ds 미리보기처럼 데이터가 없는 자리에서는 넘기지 않고 섹션을 건너뜁니다 */
    placeFeed?: PlaceFeed;
    subPlaces: any[];
    hotelPlace: { name: string; address: string; lat: number; lng: number } | null;
    participants: any[];
    isCreator: boolean;
    meetingId: string;
    /** 모임의 지출 목록 (정산 매트릭스용) — payment.splits 포함 */
    payments?: any[];
    /** 송금 토글 등 변경 후 부모 데이터 새로고침 트리거 */
    onPaymentsChange?: () => void;
}

export const MeetingFinishedView: React.FC<Props> = ({
    meeting,
    placeFeed,
    subPlaces,
    hotelPlace,
    participants,
    isCreator,
    meetingId,
    payments = [],
    onPaymentsChange,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const settlementSectionRef = useRef<HTMLElement | null>(null);

    // 홈 화면의 [내역 확인] 버튼 → state.scrollToSettlement=true 로 진입 시 정산 섹션으로 자동 스크롤
    // 푸시 알림 (PAYMENT_REMINDER 등) → URL ?scrollTo=settlement 진입 시도 동일 동작.
    //   state 는 in-app 네비게이션, query string 은 외부 (push deeplink) 진입을 모두 커버.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const fromState = (location.state as any)?.scrollToSettlement;
        const fromQuery = params.get('scrollTo') === 'settlement';
        if ((fromState || fromQuery) && settlementSectionRef.current) {
            // payments fetch 후 매트릭스 렌더 직후로 한 박자 늦춤
            const id = setTimeout(() => {
                settlementSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
            return () => clearTimeout(id);
        }
    }, [location.state, location.search, payments]);
    const { showAlert, showConfirm } = useAlert();
    const { userNo: currentUserNo } = useUserInfo();
    const [removingUserNo, setRemovingUserNo] = useState<number | null>(null);
    // 정산 추가/수정 모달
    const [costModal, setCostModal] = useState<{ open: boolean; paymentId?: string; placeTag?: string }>({ open: false });
    // 정산 재촉(nudge) — 세션 동안 이미 재촉한 splitId 기록 (서버 24h 쿨다운과 별개로 즉각 비활성화)
    const [remindedSplitIds, setRemindedSplitIds] = useState<Set<number>>(new Set());
    const [remindingSplitId, setRemindingSplitId] = useState<number | null>(null);
    // 송금 deeplink 시트 — 송금하기 클릭 시 카카오페이/토스 앱 호출 + 금액 복사 + 표시만 옵션
    const [sendSheet, setSendSheet] = useState<{ open: boolean; agg?: any; pendingAmount?: number }>({ open: false });

    const handleRemoveMember = async (member: any) => {
        const targetUserNo = member.userNo || member.id;
        if (!targetUserNo) return;
        const name = member.name || member.nickname || '';
        const ok = await showConfirm({
            title: t('meetingFinished.removeConfirmTitle', { name }),
            message: t('meetingFinished.removeConfirmMsg'),
            confirmText: t('meetingFinished.removeConfirmBtn'),
        });
        if (!ok) return;
        try {
            await MeetingRepository.onDeleteMember(meetingId, Number(targetUserNo));
            await showAlert({ message: t('meetingFinished.removeDone', { name }) });
            setRemovingUserNo(Number(targetUserNo));
            // 화면 새로고침은 부모에서 — 여기서는 즉시 시각적 반영을 위해 state 로 hide
        } catch (e: any) {
            console.error('멤버 추방 실패:', e);
            await showAlert({ message: t('meetingFinished.removeFail') });
        }
    };

    const totalPayment = Number(meeting?.totalPaymentAmount || 0);
    const myBalance = Number(meeting?.myBalanceAmount || 0);
    const myUnsettled = Number(meeting?.myUnsettledAmount || 0);
    const hasUnsettled = !!meeting?.hasUnsettledPayment;

    const memberCount = participants.length;
    const summaryDateText = (() => {
        if (meeting?.meetingType === 'TRAVEL' && meeting?.travelStartDate && meeting?.travelEndDate) {
            const s = new Date(meeting.travelStartDate);
            const e = new Date(meeting.travelEndDate);
            const days = Math.ceil((e.getTime() - s.getTime()) / 86400000) + 1;
            const nights = days - 1;
            return t('meetingFinished.nightsDays', { nights, days });
        }
        return meeting?.date || '';
    })();

    const itineraryItems = (subPlaces || [])
        .filter(sp => sp.category !== 'HOTEL')
        .sort((a: any, b: any) => {
            // dayNumber 우선, 그 다음 sortOrder
            const ad = a.dayNumber || 1, bd = b.dayNumber || 1;
            if (ad !== bd) return ad - bd;
            return (a.sortOrder || 0) - (b.sortOrder || 0);
        });

    // 귀가 도우미 — 모임 종료 후에도 그날 저녁 / 다음 날 아침까지는 시작 가능
    // 기준: 종료 시각(ended_at OR schedule.endDatetime) + 12시간 이내
    // (모임 종료 후 늦은 귀가 시점에도 충분히 활성화되도록)
    const SAFE_RETURN_WINDOW_MS = 12 * 60 * 60 * 1000;
    const endRefMs = (() => {
        const endedAt = (meeting as any)?.endedAt;
        if (endedAt) return new Date(endedAt).getTime();
        const sched = (meeting as any)?.scheduleEndDatetime;
        if (sched) return new Date(sched).getTime();
        return null;
    })();
    const isSafeReturnAvailable = endRefMs != null && (Date.now() - endRefMs) <= SAFE_RETURN_WINDOW_MS;

    const handleSettlementClick = () => setCostModal({ open: true });

    // 송금 매트릭스 — 같은 사람과의 여러 건은 합산해서 한 행에 표시 + 내역은 details 로 노출
    const myUserNoNum = Number(currentUserNo) || 0;
    type LineItem = { paymentId: any; splitId: any; storeName: string; amount: number; isSent: boolean; isPaid?: boolean };
    type Aggregate = { userNo: number; name: string; totalAmount: number; allSent: boolean; items: LineItem[] };
    const receiveMap = new Map<number, Aggregate>();
    const sendMap = new Map<number, Aggregate>();
    for (const p of (payments || [])) {
        if (p.status === 'DELETED') continue;
        const splits = p.splits || [];
        const parsed = formatPaymentLine(p.storeName, { mainPlaceName: meeting?.location });
        const displayStoreName = parsed.oneLine;
        if (Number(p.paidByNo) === myUserNoNum) {
            // 내가 결제 — 다른 멤버들이 나에게 줄 돈을 사람 별로 합산
            for (const s of splits) {
                const sUserNo = Number(s.userNo);
                if (!sUserNo || sUserNo === myUserNoNum || sUserNo <= 0) continue;
                const name = safeDecode(s.userNickname || s.nickname) || t('meetingFinished.userFallback', { no: sUserNo });
                const sent = !!s.isSent || !!s.isPaid;
                const item: LineItem = {
                    paymentId: p.paymentId,
                    splitId: s.splitId,
                    storeName: displayStoreName,
                    amount: Number(s.amount || 0),
                    isSent: sent,
                    isPaid: !!s.isPaid,
                };
                const cur: Aggregate = receiveMap.get(sUserNo) || { userNo: sUserNo, name, totalAmount: 0, allSent: true, items: [] };
                cur.totalAmount += item.amount;
                cur.allSent = cur.allSent && sent;
                cur.items.push(item);
                receiveMap.set(sUserNo, cur);
            }
        } else {
            // 다른 사람 결제 — 내가 그 사람에게 줄 돈 합산
            const payerNo = Number(p.paidByNo);
            const mySplit = splits.find((s: any) => Number(s.userNo) === myUserNoNum);
            if (mySplit && payerNo) {
                const name = safeDecode(p.paidByNickname || p.creatorNickname) || t('meetingFinished.userFallback', { no: payerNo });
                const sent = !!mySplit.isSent || !!mySplit.isPaid;
                const item: LineItem = {
                    paymentId: p.paymentId,
                    splitId: mySplit.splitId,
                    storeName: displayStoreName,
                    amount: Number(mySplit.amount || 0),
                    isSent: sent,
                    isPaid: !!mySplit.isPaid,
                };
                const cur: Aggregate = sendMap.get(payerNo) || { userNo: payerNo, name, totalAmount: 0, allSent: true, items: [] };
                cur.totalAmount += item.amount;
                cur.allSent = cur.allSent && sent;
                cur.items.push(item);
                sendMap.set(payerNo, cur);
            }
        }
    }
    // totalAmount=0 인 aggregate 는 송금/수금 자체가 무의미하므로 제외
    // (예: 카페 0원 결제처럼 share 가 0 인 경우 — '0원 송금 완료' 버튼이 어색)
    const myToReceive = Array.from(receiveMap.values())
        .filter(a => a.totalAmount > 0)
        .sort((a, b) => b.totalAmount - a.totalAmount);
    const myToSend = Array.from(sendMap.values())
        .filter(a => a.totalAmount > 0)
        .sort((a, b) => b.totalAmount - a.totalAmount);

    // 한 사람에게 가야 할 모든 미송금 결제를 일괄 처리.
    // 정산 완료 전(=주최자가 paymentSettledAt 처리 안 한 상태) 에도 송금 표시는 가능하지만
    // "추가 지출이 더 등록될 수 있다" 는 경고를 한 번 띄움 — 사용자가 이해하고 진행하도록.
    // 부분 송금 (이전에 일부만 sent 상태)은 onPostMarkSent 가 isSent=false 인 것만 갱신하므로 자연스럽게 처리.
    // Promise.allSettled — 한 건 실패해도 다른 건들 결과는 반영.
    const handleMarkAllSentForAggregate = async (agg: { items: Array<{ paymentId: any; isSent: boolean; amount?: number }>; name?: string }) => {
        const targets = agg.items.filter(it => !it.isSent && it.paymentId);
        if (targets.length === 0) return;

        // 정산 완료 전이면 경고 confirm
        if (!meeting?.paymentSettledAt) {
            const pendingTotal = targets.reduce((s, it) => s + Number(it.amount || 0), 0);
            const ok = await showConfirm({
                title: t('meetingFinished.beforeSettleTitle'),
                message: t('meetingFinished.beforeSettleMsg', { amount: pendingTotal.toLocaleString('ko-KR'), count: targets.length }),
                confirmText: t('meetingFinished.sentConfirmBtn'),
                cancelText: t('meetingFinished.later'),
            });
            if (!ok) return;
        }

        const results = await Promise.allSettled(
            targets.map(it => PaymentRepository.onPostMarkSent(String(it.paymentId)))
        );
        onPaymentsChange?.();
        const failed = results.filter(r => r.status === 'rejected').length;
        if (failed > 0) {
            await showAlert({ message: t('meetingFinished.markAllFailCount', { count: failed }) });
        }
    };

    // 정산 재촉(nudge) — 받는 사람이 미송금 split 의 송금자에게 푸시 1회 발송.
    // 1 click = 1 push (그 사람의 미송금 split 중 첫 번째 사용). 서버에서 24h 쿨다운 가드.
    // UX 톤: 알림 본문은 서버에서 PAYMENT_NUDGE_VARIANTS 풀에서 랜덤 픽 — 기분 안 상하는 유쾌 톤.
    const handleRemindAggregate = async (agg: { items: Array<{ paymentId: any; splitId: any; isSent: boolean; isPaid?: boolean; amount?: number }>; name?: string }) => {
        const target = agg.items.find(it => !it.isSent && !it.isPaid && it.splitId && it.paymentId);
        if (!target) return;
        const splitIdNum = Number(target.splitId);
        if (!splitIdNum) return;
        if (remindingSplitId === splitIdNum) return;
        setRemindingSplitId(splitIdNum);
        try {
            const res: any = await PaymentRepository.onPostRemindSplit(String(target.paymentId), splitIdNum);
            const data = res?.data?.data || res?.data || {};
            if (data?.cooldownActive) {
                const nextAt = Number(data?.nextAvailableAt || 0);
                const remainingMs = Math.max(0, nextAt - Date.now());
                const hours = Math.ceil(remainingMs / (60 * 60 * 1000));
                await showAlert({
                    message: hours > 0 ? t('meetingFinished.remindCooldownHours', { hours }) : t('meetingFinished.remindCooldownSoon'),
                });
                setRemindedSplitIds(prev => new Set(prev).add(splitIdNum));
                return;
            }
            setRemindedSplitIds(prev => new Set(prev).add(splitIdNum));
            await showAlert({ message: t('meetingFinished.remindSent') });
        } catch (e: any) {
            const msg = e?.response?.data?.message || t('meetingFinished.remindFail');
            await showAlert({ message: msg });
        } finally {
            setRemindingSplitId(null);
        }
    };

    // 한 사람의 송금완료 일괄 취소
    const handleMarkAllUnsentForAggregate = async (agg: { items: Array<{ paymentId: any; isSent: boolean; isPaid?: boolean }> }) => {
        const targets = agg.items.filter(it => it.isSent && !it.isPaid && it.paymentId);
        if (targets.length === 0) return;
        const ok = await showConfirm({
            title: t('meetingFinished.cancelSentTitle'),
            message: t('meetingFinished.cancelSentMsg', { count: targets.length }),
            confirmText: t('meetingFinished.cancelSentBtn'),
        });
        if (!ok) return;
        const results = await Promise.allSettled(
            targets.map(it => PaymentRepository.onPostMarkUnsent(String(it.paymentId)))
        );
        onPaymentsChange?.();
        const failed = results.filter(r => r.status === 'rejected').length;
        if (failed > 0) {
            await showAlert({ message: t('meetingFinished.cancelFailCount', { count: failed }) });
        }
    };

    const won = (n: number) => t('meetingFinished.amountWon', { amount: Number(n || 0).toLocaleString('ko-KR') });

    /* 보낼 돈 · 받을 돈 한 사람 분(分). 펼치면 결제별 내역 */
    const renderAggregate = (agg: Aggregate, dir: 'send' | 'recv') => {
        const sentAmount = agg.items.filter(it => it.isSent).reduce((s, it) => s + Number(it.amount || 0), 0);
        const pendingAmount = agg.totalAmount - sentAmount;
        const isPartial = sentAmount > 0 && pendingAmount > 0;
        return (
            <details key={`${dir}-${agg.userNo}`} className={styles.agg}>
                <summary className={styles.aggHead}>
                    <span className={styles.aggBody}>
                        <span className={styles.aggName}>
                            {dir === 'send'
                                ? t('meetingFinished.sendToPerson', { name: agg.name })
                                : t('meetingFinished.receiveFromPerson', { name: agg.name })}
                            <strong className={agg.allSent ? styles.aggDone : styles.aggAmount}>
                                {won(dir === 'send' && isPartial ? pendingAmount : agg.totalAmount)}
                            </strong>
                            {dir === 'send' && isPartial && (
                                <span className={styles.aggNote}>
                                    {t('meetingFinished.alreadySent', { amount: sentAmount.toLocaleString('ko-KR') })}
                                </span>
                            )}
                        </span>
                        <span className={styles.aggMeta}>
                            {t('meetingFinished.itemsDetail', { count: agg.items.length })}
                        </span>
                    </span>

                    {dir === 'send' ? (
                        <Button
                            size="sm"
                            variant={agg.allSent ? 'ghost' : 'primary'}
                            onClick={(e) => {
                                e.preventDefault();
                                if (agg.allSent) handleMarkAllUnsentForAggregate(agg);
                                else setSendSheet({ open: true, agg, pendingAmount });
                            }}
                            title={agg.allSent
                                ? t('meetingFinished.cancelSentBtnTitle')
                                : (isPartial
                                    ? t('meetingFinished.partialSendTitle', { amount: pendingAmount.toLocaleString('ko-KR') })
                                    : t('meetingFinished.chooseSendMethod'))}
                        >
                            {agg.allSent
                                ? t('meetingFinished.sentLabel')
                                : isPartial
                                    ? t('meetingFinished.amountSend', { amount: pendingAmount.toLocaleString('ko-KR') })
                                    : t('meetingFinished.send')}
                        </Button>
                    ) : (
                        <>
                            <span className={agg.allSent ? `${styles.state} ${styles.stateDone}` : styles.state}>
                                {agg.allSent ? t('meetingFinished.received') : t('meetingFinished.waitingRecv')}
                            </span>
                            {!agg.allSent && (() => {
                                const firstUnsent = agg.items.find(it => !it.isSent && !it.isPaid && it.splitId);
                                if (!firstUnsent) return null;
                                const sId = Number(firstUnsent.splitId);
                                const reminded = remindedSplitIds.has(sId);
                                const inFlight = remindingSplitId === sId;
                                return (
                                    <Button
                                        size="sm"
                                        variant="secondary"
                                        disabled={reminded || inFlight}
                                        title={reminded ? t('meetingFinished.remindDoneTitle') : t('meetingFinished.remindTitle')}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleRemindAggregate(agg);
                                        }}
                                    >
                                        {reminded ? t('meetingFinished.remindSentShort') : t('meetingFinished.remind')}
                                    </Button>
                                );
                            })()}
                        </>
                    )}
                </summary>

                <ul className={styles.aggItems}>
                    {agg.items.map(it => (
                        <li key={it.splitId || it.paymentId} className={styles.aggItem}>
                            <span className={styles.aggItemName}>{it.storeName}</span>
                            <span className={styles.aggItemAmount}>{won(it.amount)}</span>
                            <span className={it.isSent ? `${styles.state} ${styles.stateDone}` : styles.state}>
                                {it.isSent
                                    ? (dir === 'send' ? t('meetingFinished.sentStatus') : t('meetingFinished.received'))
                                    : t('meetingFinished.waiting')}
                            </span>
                        </li>
                    ))}
                </ul>
            </details>
        );
    };

    /* 히어로 배경 사진 — 가게 사진 → 방문 사진 순.
       외부 제공자 사진은 이 화면이 들고 있지 않아, 우리가 모은 것만 씁니다.
       하나도 없으면 HeroBanner 가 먹색 면으로 떨어집니다. */
    const heroImages = React.useMemo(() => feedPhotos(placeFeed), [placeFeed]);

    const splitPayments = (payments || []).filter((p: any) => p.status !== 'DELETED' && (p.splits || []).length > 0);

    return (
        <AppScreen title={meeting?.name || t('meetingFinished.meetingFallback')} bar="sub">
            {/* ① 무슨 모임이었는지 — 진행 중 화면과 같은 히어로.
                사진이 없으면 HeroBanner 가 먹색 면으로 떨어져 글자는 그대로 읽힙니다.
                여기만 글자로 두면 같은 모임인데 화면마다 첫인상이 달라집니다. */}
            <Bleed>
                <HeroBanner
                    imageUrl={heroImages[0]}
                    photos={heroImages}
                    tags={[
                        { label: t('meetingFinished.endedBadge') },
                        ...(meeting?.paymentSettledAt ? [{ label: t('meetingFinished.closed') }] : []),
                    ]}
                    title={meeting?.name}
                    paragraph={
                        t('meetingFinished.memberCount', { count: memberCount })
                        + (totalPayment > 0
                            ? ` · ${t('meetingFinished.usedAmount', { amount: totalPayment.toLocaleString('ko-KR') })}`
                            : '')
                    }
                    date={summaryDateText || undefined}
                />
            </Bleed>

            {/* 다녀온 뒤라 이 자리가 사진·후기 자리입니다.
                기억이 남아 있을 때 남겨야 다음 사람이 고를 때 도움이 됩니다. */}
            {meeting?.placeKey && placeFeed ? (
                <Section spacing="tight">
                    <PlacePhotoPeek
                        placeId={meeting.placeKey}
                        placeName={meeting.location || meeting.placeName || ''}
                        meetingId={meetingId}
                        canAdd
                        allowWrite
                        feed={placeFeed}
                    />
                </Section>
            ) : null}

            {/* 안전 귀가 — 종료 후 12시간 안에만. 이 화면에서 지금 눌러야 할 단 하나 */}
            {isSafeReturnAvailable && (
                <Section spacing="tight">
                    <InverseBanner
                        title={t('meetingFinished.safeReturnStart')}
                        sub={t('meetingFinished.safeReturnDesc')}
                        onClick={() => navigate(`/safe-return/start/${meetingId}`)}
                    />
                </Section>
            )}

            {/* ② 어디를 어떻게 다녔는지 */}
            <Section divided>
                <SectionHeader title={t('meetingFinished.progress')} />
                {/* 대표 장소도 하나의 방문지입니다. 판정에서 빼면 서브 장소를 안 넣은
                    모임이 "등록된 장소가 없어요" 로 보입니다 — 바로 아래에서 그리고 있는데도. */}
                {itineraryItems.length === 0 && !hotelPlace && !meeting?.location ? (
                    <p className={styles.empty}>{t('meetingFinished.noPlaces')}</p>
                ) : (
                    <ol className={styles.timeline}>
                        {meeting?.location && (
                            <TimelineItem
                                time={meeting?.time || ''}
                                category={t('meetingFinished.gather')}
                                name={meeting.location}
                                address={meeting.locationDetail || ''}
                            />
                        )}
                        {itineraryItems.map((sp: any) => (
                            <TimelineItem
                                key={sp.subPlaceId}
                                time={sp.visitTime || ''}
                                day={meeting?.meetingType === 'TRAVEL' ? sp.dayNumber : undefined}
                                category={categoryLabel(sp.category)}
                                name={sp.placeName}
                                address={sp.placeAddress || ''}
                            />
                        ))}
                        {hotelPlace && (
                            <TimelineItem
                                time=""
                                category={t('meetingFinished.hotel')}
                                name={hotelPlace.name}
                                address={hotelPlace.address}
                            />
                        )}
                    </ol>
                )}
            </Section>

            {/* ③ 정산 */}
            <Section divided>
                <span ref={settlementSectionRef as React.RefObject<HTMLSpanElement>} />
                <SectionHeader title={t('meetingFinished.settlement')} />

                {totalPayment === 0 ? (
                    <>
                        <p className={styles.empty}>{t('meetingFinished.noExpense')}</p>
                        <Button block onClick={handleSettlementClick}>{t('meetingFinished.enterExpense')}</Button>
                    </>
                ) : (
                    <>
                        <dl className={styles.sums}>
                            <div className={styles.sum}>
                                <dt className={styles.sumLabel}>{t('meetingFinished.totalSpent')}</dt>
                                <dd className={styles.sumValue}>{won(totalPayment)}</dd>
                            </div>
                            {myBalance > 0 && (
                                <div className={styles.sum}>
                                    <dt className={styles.sumLabel}>{t('meetingFinished.toReceiveLabel')}</dt>
                                    <dd className={`${styles.sumValue} ${styles.sumStrong}`}>{won(myBalance)}</dd>
                                </div>
                            )}
                            {myUnsettled > 0 && (
                                <div className={styles.sum}>
                                    <dt className={styles.sumLabel}>{t('meetingFinished.toSendLabel')}</dt>
                                    <dd className={`${styles.sumValue} ${styles.sumDue}`}>{won(myUnsettled)}</dd>
                                </div>
                            )}
                        </dl>

                        {!hasUnsettled && myBalance === 0 && myUnsettled === 0 && (
                            <p className={styles.note}>{t('meetingFinished.settleDone')}</p>
                        )}

                        {(myToSend.length > 0 || myToReceive.length > 0 || splitPayments.length > 0) && (
                            <>
                                {!meeting?.paymentSettledAt && (
                                    <p className={styles.warn}>{t('meetingFinished.beforeSettleWarn')}</p>
                                )}

                                {myToSend.length > 0 && (
                                    <>
                                        <p className={styles.subTitle}>
                                            {t('meetingFinished.mySendTitle', { count: myToSend.length })}
                                        </p>
                                        <div className={styles.aggList}>
                                            {myToSend.map(agg => renderAggregate(agg, 'send'))}
                                        </div>
                                    </>
                                )}

                                {myToReceive.length > 0 && (
                                    <>
                                        <p className={styles.subTitle}>
                                            {t('meetingFinished.myReceiveTitle', { count: myToReceive.length })}
                                        </p>
                                        <div className={styles.aggList}>
                                            {myToReceive.map(agg => renderAggregate(agg, 'recv'))}
                                        </div>
                                    </>
                                )}

                                {/* 전체 송금 명세 — 결제 단위, 모든 멤버에게 공개 */}
                                {splitPayments.length > 0 && (
                                    <>
                                        <p className={styles.subTitle}>{t('meetingFinished.allSendDetail')}</p>
                                        <div className={styles.payList}>
                                            {splitPayments.map((p: any) => {
                                                const splits = (p.splits || []).filter((s: any) => s.userNo > 0);
                                                const isPayerMe = Number(p.paidByNo) === myUserNoNum;
                                                const canEdit = isPayerMe || Number(p.creatorNo) === myUserNoNum || isCreator;
                                                return (
                                                    <div key={p.paymentId} className={styles.pay}>
                                                        <div className={styles.payHead}>
                                                            <span className={styles.payName}>
                                                                {formatPaymentLine(p.storeName, { mainPlaceName: meeting?.location }).oneLine}
                                                            </span>
                                                            <span className={p.isTreat ? `${styles.payAmount} ${styles.payTreat}` : styles.payAmount}>
                                                                {formatPaymentAmount(p)}
                                                            </span>
                                                            {canEdit && !meeting?.paymentSettledAt && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="secondary"
                                                                    onClick={() => setCostModal({ open: true, paymentId: p.paymentId })}
                                                                >
                                                                    {t('meetingFinished.editBtn')}
                                                                </Button>
                                                            )}
                                                        </div>
                                                        <p className={styles.payMeta}>
                                                            {t('meetingFinished.payLabel')}: <strong>{safeDecode(p.paidByNickname) || '?'}</strong>
                                                            {isPayerMe && <> ({t('meetingFinished.me')})</>}
                                                            {' · '}{t('meetingFinished.splitCount', { count: splits.length })}
                                                        </p>
                                                        <ul className={styles.splitList}>
                                                            {splits.map((s: any) => {
                                                                const isMe = Number(s.userNo) === myUserNoNum;
                                                                const isPayerSplit = Number(s.userNo) === Number(p.paidByNo);
                                                                const sent = !!s.isSent || !!s.isPaid;
                                                                return (
                                                                    <li key={s.splitId} className={isMe ? `${styles.split} ${styles.splitMe}` : styles.split}>
                                                                        <span className={styles.splitName}>
                                                                            {safeDecode(s.userNickname || s.nickname) || t('meetingFinished.userFallback', { no: s.userNo })}
                                                                            {isMe && <> ({t('meetingFinished.me')})</>}
                                                                        </span>
                                                                        <span className={styles.splitAmount}>{won(s.amount)}</span>
                                                                        <span className={
                                                                            isPayerSplit ? styles.state
                                                                                : sent ? `${styles.state} ${styles.stateDone}`
                                                                                    : `${styles.state} ${styles.stateWait}`
                                                                        }>
                                                                            {isPayerSplit ? t('meetingFinished.payer')
                                                                                : sent ? t('meetingFinished.sentStatus')
                                                                                    : t('meetingFinished.waiting')}
                                                                        </span>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        <div className={styles.actions}>
                            {!meeting?.paymentSettledAt && (
                                <Button block onClick={handleSettlementClick}>
                                    {t('meetingFinished.addNewExpense')}
                                </Button>
                            )}
                            {/* 쓴 돈이 없어도 마칠 수 있어야 합니다. totalPayment > 0 을
                                걸어 두면 지출 0건인 모임은 정산 완료를 못 찍어 홈
                                「정산 대기 중」에서 영영 빠져나오지 못합니다. */}
                            {isCreator && !meeting?.paymentSettledAt && (
                                <Button
                                    variant="secondary"
                                    block
                                    onClick={async () => {
                                        const ok = await showConfirm({
                                            title: t('meetingFinished.settleConfirmTitle'),
                                            message: t(totalPayment > 0
                                                ? 'meetingFinished.settleConfirmMsg'
                                                : 'meetingFinished.settleNothingConfirmMsg'),
                                            confirmText: t('meetingFinished.settleConfirmBtn'),
                                        });
                                        if (!ok) return;
                                        try {
                                            await MeetingRepository.onPostSettleMeeting(meetingId);
                                            await showAlert({ message: t('meetingFinished.settleDoneMsg') });
                                            window.location.reload();
                                        } catch (e: any) {
                                            console.error('정산 완료 실패:', e);
                                            await showAlert({ message: t('meetingFinished.settleFail') });
                                        }
                                    }}
                                >
                                    {t(totalPayment > 0
                                        ? 'meetingFinished.settleAction'
                                        : 'meetingFinished.settleNothingAction')}
                                </Button>
                            )}
                            {meeting?.paymentSettledAt && isCreator && (
                                <Button
                                    variant="danger"
                                    block
                                    onClick={async () => {
                                        const ok = await showConfirm({
                                            title: t('meetingFinished.cancelSettleTitle'),
                                            message: t('meetingFinished.cancelSettleMsg'),
                                            confirmText: t('meetingFinished.cancelSettleBtn'),
                                        });
                                        if (!ok) return;
                                        try {
                                            await MeetingRepository.onDeleteSettleMeeting(meetingId);
                                            await showAlert({ message: t('meetingFinished.settleCancelled') });
                                            window.location.reload();
                                        } catch {
                                            await showAlert({ message: t('meetingFinished.settleCancelFail') });
                                        }
                                    }}
                                >
                                    {t('meetingFinished.cancelSettleAction')}
                                </Button>
                            )}
                            {!isCreator && !meeting?.paymentSettledAt && (
                                <p className={styles.warn}>{t('meetingFinished.waitSettleNote')}</p>
                            )}
                        </div>
                    </>
                )}
            </Section>

            {/* ④ 누가 왔는지 */}
            <Section divided>
                <SectionHeader title={t('meetingFinished.participants', { count: memberCount })} />
                <ul className={styles.people}>
                    {participants.filter((p: any) => Number(p.userNo || p.id) !== removingUserNo).map((p: any) => {
                        const targetUserNo = Number(p.userNo || p.id);
                        const canRemove = isCreator && !p.isCreator && targetUserNo > 0;
                        return (
                            <li key={p.id || p.userNo} className={styles.person}>
                                <Avatar
                                    size="sm"
                                    person={{
                                        userNo: p.userNo || p.id || 0,
                                        name: p.name || p.nickname || '?',
                                        imageUrl: p.profileImgUrl,
                                        attendance: p.status === 'confirmed' ? 'going'
                                            : p.status === 'declined' ? 'declined' : 'pending',
                                        isHost: !!p.isCreator,
                                    }}
                                />
                                <span className={styles.personBody}>
                                    <span className={styles.personName}>
                                        {p.name || p.nickname}
                                        {p.isCreator && <span className={styles.personHost}>{t('meetingFinished.host')}</span>}
                                    </span>
                                </span>
                                <span className={styles.state}>
                                    {p.status === 'confirmed' ? t('meetingFinished.attend')
                                        : p.status === 'declined' ? t('meetingFinished.decline') : '—'}
                                </span>
                                {canRemove && (
                                    <Button size="sm" variant="ghost" onClick={() => handleRemoveMember(p)}>
                                        {t('meetingFinished.removeBtn')}
                                    </Button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </Section>

            {/* ⑤ 후기 */}
            <MeetingPostsSection
                meetingId={meetingId}
                currentUserNo={Number(currentUserNo) || 0}
                isCreator={isCreator}
            />

            {/* 정산 등록/수정 모달 */}
            {costModal.open && (
                <CostInput
                    inModal
                    meetingIdProp={meetingId}
                    paymentIdProp={costModal.paymentId}
                    placeTagProp={costModal.placeTag}
                    onClose={() => setCostModal({ open: false })}
                    onSaved={() => onPaymentsChange?.()}
                />
            )}

            {/* 송금 시트 — 카카오페이/토스는 수신자 정보 없이 송금 화면으로 못 보냅니다(보이스피싱 방지).
                그래서 금액 복사를 1차 동작으로 두고, 보낸 뒤 [송금 완료 처리] 로 표시합니다. */}
            <Sheet
                open={!!(sendSheet.open && sendSheet.agg)}
                title={t('meetingFinished.sendGuideTitle')}
                closeLabel={t('meetingFinished.close')}
                onClose={() => setSendSheet({ open: false })}
            >
                <p className={styles.sheetLead}>
                    <strong className={styles.sumDue}>{won(sendSheet.pendingAmount || 0)}</strong>
                    {sendSheet.agg?.name
                        ? <> · {t('meetingFinished.sendGuideToPerson', { name: sendSheet.agg.name })}</>
                        : null}
                </p>
                <p className={styles.sheetGuide}>
                    {t('meetingFinished.sendGuideBody1')}
                    <br />
                    <strong>{t('meetingFinished.step1')}</strong> → <strong>{t('meetingFinished.step2')}</strong> → <strong>{t('meetingFinished.step3')}</strong>
                    {t('meetingFinished.sendGuideAfter')}
                    <strong>{t('meetingFinished.sendGuideAction')}</strong>
                    {t('meetingFinished.sendGuideTail')}
                </p>
                <div className={styles.actions}>
                    <Button
                        block
                        onClick={async () => {
                            const amount = sendSheet.pendingAmount || 0;
                            try {
                                await navigator.clipboard.writeText(String(amount));
                                await showAlert({ message: t('meetingFinished.amountCopied', { amount: amount.toLocaleString('ko-KR') }) });
                            } catch {
                                await showAlert({ message: t('meetingFinished.copyFail') });
                            }
                        }}
                    >
                        {t('meetingFinished.copyAmount')}
                    </Button>
                    <Button
                        variant="secondary"
                        block
                        onClick={() => {
                            const agg = sendSheet.agg;
                            setSendSheet({ open: false });
                            handleMarkAllSentForAggregate(agg);
                        }}
                    >
                        {t('meetingFinished.markSentDone')}
                    </Button>
                </div>
            </Sheet>
        </AppScreen>
    );
};

interface TimelineItemProps {
    time: string;
    day?: number;
    category: string;
    name: string;
    address: string;
}

/** 지나온 자리 한 줄 — 왼쪽에 세로선, 시각·분류·이름·주소 */
const TimelineItem: React.FC<TimelineItemProps> = ({ time, day, category, name, address }) => (
    <li className={styles.step}>
        <span className={styles.stepMeta}>
            {day ? <span className={styles.stepDay}>DAY {day}</span> : null}
            {time ? <span className={styles.stepTime}>{time}</span> : null}
            <span className={styles.stepCategory}>{category}</span>
        </span>
        <span className={styles.stepName}>{name}</span>
        {address && <span className={styles.stepAddress}>{address}</span>}
    </li>
);

const categoryLabel = (cat: string): string => {
    const keys: Record<string, string> = {
        PRIMARY: 'category.primary', SECONDARY: 'category.secondary', TERTIARY: 'category.tertiary',
        ATTRACTION: 'category.attraction', RESTAURANT: 'category.restaurant', CAFE: 'category.cafe', HOTEL: 'category.hotel',
    };
    return keys[cat] ? i18n.t(keys[cat]) : (cat || i18n.t('meetingFinished.placeGeneric'));
};

export default MeetingFinishedView;
